import { useEffect, useState } from "react";
import { Info, Clock, MessageSquare, FileText, Plug, Activity, SquareTerminal } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate, fmtDuration } from "@/lib/api";
import { InfoTab } from "@/components/InfoTab";
import { HelpCard } from "@/components/HelpCard";
import { Timeline } from "@/components/machine/Timeline";
import { ChatTab, CommandsTab } from "@/components/machine/LiveTabs";

const TABS = [
  { id: "info", icon: Info, label: "Informations" },
  { id: "history", icon: Clock, label: "Connexions" },
  { id: "chat", icon: MessageSquare, label: "Messages" },
  { id: "commands", icon: SquareTerminal, label: "Commandes" },
  { id: "notes", icon: FileText, label: "Notes" },
];

export const NotesTab = ({ m, onSaved }) => {
  const [notes, setNotes] = useState(m.notes || "");
  useEffect(() => setNotes(m.notes || ""), [m.machine_id, m.notes]);
  const save = async () => {
    try {
      await api.patch(`/machines/${m.machine_id}`, { notes });
      toast.success("Notes enregistrées");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="machine-notes-tab">
      <Textarea data-testid="machine-notes-textarea" rows={12} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Mot de passe Wi-Fi, imprimante, interventions…" />
      <Button data-testid="save-notes-btn" className="mt-3" onClick={save}>Enregistrer les notes</Button>
    </div>
  );
};

export const DetailPanel = ({ latest, m, group, onConnect, onSaved, canConnect, help, onHelpResolved }) => {
  const [tab, setTab] = useState("info");
  if (!m) return <section className="w-[520px] flex-shrink-0 grid place-items-center text-slate-400 text-sm" data-testid="detail-empty">Sélectionnez une machine</section>;
  return (
    <section className="w-[520px] flex-shrink-0 flex flex-col min-h-0" data-testid="machine-detail-panel">
      <div className="h-[72px] flex items-center justify-between px-6 border-b border-slate-200">
        <h2 className="text-[22px] truncate" data-testid="detail-machine-name">{m.display_name || m.hostname}</h2>
        <div className="flex items-center gap-1">
          <Button asChild size="sm" variant="ghost"><Link to={`/app/rmm/${m.machine_id}`} data-testid="detail-rmm-btn"><Activity className="h-4 w-4 mr-1" /> RMM</Link></Button>
          <Button size="sm" variant="ghost" disabled={!m.online || !canConnect} onClick={() => onConnect(m)} data-testid="detail-connect-btn"><Plug className="h-4 w-4 mr-1" /> Participer</Button>
        </div>
      </div>
      {help && <HelpCard reqs={help} m={m} onConnect={onConnect} canConnect={canConnect} onResolved={onHelpResolved} />}
      <div className="flex-1 min-h-0 flex">
        <nav className="w-14 bg-[#F1F3F6] border-r border-slate-200 flex flex-col">
          {TABS.map(({ id, icon: Icon, label }) => (
            <button key={id} title={label} data-testid={`detail-tab-${id}`} onClick={() => setTab(id)} className={`h-14 grid place-items-center ${tab === id ? "bg-white shadow-[inset_3px_0_0_#2563EB] text-slate-900" : "text-slate-600 hover:bg-slate-200"}`}>
              <Icon className="h-6 w-6" strokeWidth={1.6} />
            </button>
          ))}
        </nav>
        <div className="flex-1 overflow-y-auto p-5">
          {tab === "info" && <InfoTab m={m} group={group} latest={latest} />}
          {tab === "history" && <Timeline id={m.machine_id} />}
          {tab === "chat" && <ChatTab m={m} />}
          {tab === "commands" && <CommandsTab m={m} />}
          {tab === "notes" && <NotesTab m={m} onSaved={onSaved} />}
        </div>
      </div>
    </section>
  );
};
